import React, { useState, useEffect } from 'react';
import { 
    Activity, ShieldCheck, ShieldAlert, Cpu, 
    RefreshCw, Globe, Zap, Server, 
    AlertTriangle, CheckCircle, Info,
    HardDrive, Signal, Clock, Database,
    Wand2, Search, Target, Fingerprint
} from 'lucide-react';

interface AIStatus {
    connected: boolean;
    model?: string;
    latency?: string;
    statusText?: string;
    message?: string;
    error?: string;
    details?: string;
    timestamp: string;
}

export const AiStatusManager: React.FC = () => {
    const [status, setStatus] = useState<AIStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkStatus = async () => {
        setIsLoading(true);
        try {
            const res = await fetch('/practice-exams/api/ai/status');
            const data = await res.json();
            setStatus(data);
        } catch (e) {
            setStatus({
                connected: false,
                error: "Network / Proxy Error",
                details: "Failed to communicate with the internal AI proxy endpoint. Verify server is running and accessible.",
                timestamp: new Date().toISOString()
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkStatus();
    }, []);

    return (
        <div className="space-y-8 animate-fade-in pb-12 max-w-6xl mx-auto text-slate-900">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                            <Cpu size={16} />
                        </div>
                        <span className="text-[10px] font-black tracking-widest text-slate-400">Core Telemetry</span>
                    </div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tight">AI Engine Status</h2>
                    <p className="text-slate-500 font-medium mt-1">Real-time health monitoring for Google Gemini integration.</p>
                </div>
                <button 
                    onClick={checkStatus}
                    disabled={isLoading}
                    className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-3 shadow-xl hover:bg-slate-800 transition-all active:scale-95 disabled:opacity-50"
                >
                    <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} /> 
                    {isLoading ? 'Polling API...' : 'Refresh Status'}
                </button>
            </div>

            {/* Main Health Card */}
            <div className={`p-10 rounded-[3rem] border-2 shadow-2xl relative overflow-hidden transition-all duration-700 ${status?.connected ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                {/* Visual Background Pulse */}
                <div className={`absolute top-0 right-0 w-96 h-96 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 opacity-20 pointer-events-none ${status?.connected ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-4 flex flex-col items-center text-center">
                        <div className={`w-32 h-32 rounded-[2.5rem] flex items-center justify-center shadow-2xl mb-6 ring-8 transition-all ${status?.connected ? 'bg-emerald-500 text-white ring-emerald-500/10' : 'bg-rose-500 text-white ring-rose-500/10 animate-pulse'}`}>
                            {status?.connected ? <CheckCircle size={56} /> : <ShieldAlert size={56} />}
                        </div>
                        <h3 className={`text-2xl font-black tracking-tight ${status?.connected ? 'text-emerald-900' : 'text-rose-900'}`}>
                            {status?.connected ? 'Systems Operational' : 'Engine Interrupted'}
                        </h3>
                        <p className={`text-[10px] font-black tracking-[0.3em] mt-2 ${status?.connected ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {status?.connected ? 'Bridge Verified' : 'Critical Config Failure'}
                        </p>
                    </div>

                    <div className="lg:col-span-8 space-y-6">
                        {status?.connected ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <StatusEntry icon={Database} label="Assigned Model" val={status.model || 'Unknown'} />
                                <StatusEntry icon={Clock} label="Engine Latency" val={status.latency || 'N/A'} />
                                <StatusEntry icon={Globe} label="Region" val="US-Central (Global)" />
                                <StatusEntry icon={Target} label="Logic Result" val={status.statusText || 'Verified'} />
                                <div className="md:col-span-2 p-6 bg-white/50 rounded-3xl border border-emerald-200">
                                    <p className="text-[10px] font-black text-emerald-700 tracking-widest mb-2 flex items-center gap-2">
                                        <ShieldCheck size={14}/> Operational Logic
                                    </p>
                                    <p className="text-sm font-bold text-emerald-900">"AI connectivity is healthy. All Tutor, Factory, and Simulator modules are pulling data from the primary inference edge."</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="p-8 bg-white border-2 border-rose-200 rounded-[2rem] shadow-xl">
                                    <div className="flex items-center gap-3 mb-4 text-rose-600">
                                        <AlertTriangle size={24} />
                                        <h4 className="text-lg font-black tracking-tight">Diagnosis: {status?.error || 'Unknown'}</h4>
                                    </div>
                                    <p className="text-slate-600 font-bold leading-relaxed">{status?.details || 'No error details provided by system.'}</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="p-4 bg-rose-100/50 rounded-2xl border border-rose-200">
                                        <p className="text-[9px] font-black text-rose-700">Recovery Path</p>
                                        <p className="text-xs font-bold text-rose-900 mt-1">Ensure the API_KEY environment variable is correctly set in your deployment configuration (e.g., app.yaml or GCP Secrets).</p>
                                    </div>
                                    <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800">
                                        <p className="text-[9px] font-black text-slate-500">System Timestamp</p>
                                        <p className="text-xs font-bold text-white mt-1">{status?.timestamp || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Technical Telemetry Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6 group hover:border-indigo-500 transition-all">
                    <div className="flex items-center justify-between">
                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                            <Zap size={20}/>
                        </div>
                        <span className={`text-[9px] font-black ${status?.connected ? 'text-emerald-500' : 'text-slate-300'}`}>
                            {status?.connected ? 'Stable' : 'Muted'}
                        </span>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-black text-slate-400 tracking-widest mb-1">Inference Speed</h4>
                        <p className="text-2xl font-black text-slate-900">{status?.latency || '---'}</p>
                    </div>
                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: status?.connected ? '85%' : '0%' }}></div>
                    </div>
                </div>

                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6 group hover:border-medical-500 transition-all">
                    <div className="flex items-center justify-between">
                        <div className="p-3 bg-medical-50 text-medical-600 rounded-xl group-hover:scale-110 transition-transform">
                            <Signal size={20}/>
                        </div>
                        <span className={`text-[9px] font-black ${status?.connected ? 'text-slate-400' : 'text-rose-400'}`}>
                            {status?.connected ? 'Active' : 'Offline'}
                        </span>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-black text-slate-400 tracking-widest mb-1">Protocol Model</h4>
                        <p className="text-xl font-black text-slate-900 truncate">{status?.model || 'Disconnected'}</p>
                    </div>
                    <div className="flex gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${status?.connected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-200'}`}></div>
                        <div className={`w-2 h-2 rounded-full ${status?.connected ? 'bg-emerald-500 animate-pulse delay-75' : 'bg-slate-200'}`}></div>
                        <div className="w-2 h-2 rounded-full bg-slate-200"></div>
                    </div>
                </div>

                <div className="bg-slate-900 p-8 rounded-[2rem] text-white shadow-xl space-y-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                        <Server size={80} />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="p-3 bg-white/5 border border-white/10 text-medical-400 rounded-xl">
                            <Activity size={20}/>
                        </div>
                        <span className="text-[9px] font-black text-slate-500">Uptime</span>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-black text-slate-500 tracking-widest mb-1">Edge Availability</h4>
                        <p className="text-2xl font-black text-white">99.9%</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[8px] font-black text-medical-400">Live Traffic</span>
                        <div className="flex-1 h-px bg-white/10"></div>
                    </div>
                </div>
            </div>

            {/* Bottom Insight Area */}
            <div className="bg-white rounded-[2.5rem] p-10 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-10 items-center">
                <div className="w-20 h-20 bg-slate-50 rounded-[1.8rem] flex items-center justify-center text-slate-300 shrink-0 shadow-inner">
                    <ShieldCheck size={32} />
                </div>
                <div className="flex-1 space-y-2">
                    <h3 className="text-xl font-black text-slate-900 tracking-tight">Security Audit</h3>
                    <p className="text-sm text-slate-500 font-medium leading-relaxed opacity-95">The AI engine uses an isolated environment for all logical operations. API keys are managed via deployment environment variables to ensure they are never exposed to the client-side JavaScript. This page provides an administrative window into that secure bridge.</p>
                </div>
                <div className="shrink-0 flex items-center gap-4 px-6 py-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <Fingerprint className="text-indigo-600" />
                    <span className="text-[10px] font-black tracking-widest">TLS 1.3 Certified</span>
                </div>
            </div>
        </div>
    );
};

const StatusEntry: React.FC<{ icon: any, label: string, val: string }> = ({ icon: Icon, label, val }) => (
    <div className="p-4 bg-white/60 rounded-2xl border border-slate-200/50 flex items-center gap-4 shadow-sm hover:shadow-md transition-all group">
        <div className="p-2.5 bg-slate-900 text-white rounded-xl shadow-lg group-hover:scale-110 transition-transform">
            <Icon size={16} />
        </div>
        <div>
            <p className="text-[8px] font-black text-slate-400 tracking-widest">{label}</p>
            <p className="text-[11px] font-black text-slate-800 truncate max-w-[150px]">{val}</p>
        </div>
    </div>
);